/**
 * CloudMeshField — clouds as REAL 3D MESHES in the world, not a screen-space
 * composite.
 *
 * WHY (2026-08-19 field directive): the post-pass raymarch slab (CloudRenderer)
 * is a screen-space effect — even with physically-correct world anchoring
 * (parallaxScale 1) a 3.8 km deck parallaxes only ~0.75° per 50 m walked
 * (moon-follows-the-car), and players kept reading it as "the clouds follow
 * my camera". This module instead spawns a pooled set of sphere meshes at
 * world positions derived from the weather map; each mesh raymarches the
 * engine's baked 3D noise INSIDE ITS OWN VOLUME in the fragment shader (the
 * classic volume-renderer "raymarch in a sphere" pattern — camera ray vs
 * analytic sphere bound, march between entry/exit). Because they are scene
 * geometry they get:
 *   - TRUE parallax for free (walk 50 m and a 900 m-altitude puff swings
 *     ~3° against the towers — unmistakably stationary in the world);
 *   - depth-tested occlusion against buildings both ways;
 *   - natural frustum culling + transparent sorting from three.js.
 *
 * Architecture:
 *   - CPU "chunk streamer": the field radius around the camera is divided
 *     into cells (default 700 m); each cell is deterministically hashed to a
 *     cloud (position jitter / radius / altitude / noise seed) and sampled
 *     against the CPU copy of the weather map. Cells that pass the coverage
 *     gate (nearest-first, up to maxClouds) get a pooled mesh. The mapping
 *     is stable as the camera moves, so clouds persist when you leave and
 *     return — they are world objects.
 *   - Wind scrolls the noise INSIDE each volume (same convention as the
 *     deck: metres of pattern drift), plus the net-zero evolution counter-
 *     drift morphs shapes in place. Meshes themselves stay at their cell
 *     home, so pooling stays trivial and rebase-safe.
 *   - FP64 origin rebase: shader noise UVs use ABSOLUTE world position
 *     (positionWorld − worldOffset, worldOffset = scene.position — the same
 *     convention as CloudRenderer); mesh centers are re-fed per frame, so a
 *     rebase snap moves clouds WITH the world, never re-arranging them.
 *
 * Anti-pop: new assignments fade in over ~3 s (per-mesh uBirth) and everything
 * beyond fadeStart is fully transparent, so recycles at the field edge are
 * invisible.
 *
 * Blending: premultiplied OVER — the march outputs the same {rgb premult, a}
 * contract as CloudRenderer; the material uses CustomBlending One / OneMinus-
 * SrcAlpha so `final = rgb + dst·(1−a)` exactly like the post-pass composite.
 * depthWrite off, BackSide (so the fragment position is the sphere EXIT point
 * and camera-inside-volume still rasterizes), depthTest on (towers occlude).
 *
 * v1 simplifications (documented, deliberate):
 *   - No scene-depth clamp inside the march: a tower poking INTO a volume
 *     slices it hard (the old sky-mask composite had the same hard edge).
 *     Soft-depth fade via viewportDepthTexture is the roadmap item.
 *   - Sphere bound (not ellipsoid); flat/wide shapes come from the noise
 *     anisotropy + height envelope, not the mesh scale.
 *   - Wind scrolls the pattern; meshes don't translate. Keeps cell pooling
 *     stable; reads identically at cloud distances.
 */

import * as THREE from 'three/webgpu';
import { markMrtOverlay } from '../post/mrtOverlay.js';
import {
  Fn, If, vec3, vec4, float, uniform,
  max, int, clamp, mix,
  normalize, distance, smoothstep, texture3D, uniformArray,
  positionWorld, cameraPosition,
} from 'three/tsl';
import { mergeGeometries } from 'three/addons/utils/BufferGeometryUtils.js';
import { cloudLobes, MAX_LOBES, type CloudLobe } from './CloudLobes.js';
import { buildCloudMarch, type CloudMarchEnv, cloudAir } from './CloudMarch.js';


export interface CloudMeshFieldConfig {
  /** Half-width of the cloud field around the camera (m). Default 9000. */
  fieldRadius?: number;
  /** Streaming cell size (m). Default 700. */
  cellSize?: number;
  /** Max simultaneous cloud meshes (pool size). Default 40. */
  maxClouds?: number;
  /**
   * The CONDENSATION LEVEL (m): every cloud's flat BASE sits at this
   * altitude (±baseJitter). Real cumulus bases align at the lifting
   * condensation level — "neighbouring cumulus develop bases at similar
   * levels" — while TOPS vary with cloud type; randomizing center
   * altitudes (the first cut) scattered the bases and read as balloons.
   * Default 750.
   */
  baseAltitude?: number;
  /** Per-cloud base-altitude jitter (m). Default 40. */
  baseJitter?: number;
  /** Cloud bounding-sphere radius range (m). Default [220, 520]. */
  radiusMin?: number;
  radiusMax?: number;
  /**
   * 3D noise UV per metre inside a volume. Default 0.0038 (~260 m base
   * period) — a 400-500 m puff spans ~2 lobes, so clouds read as cauliflower
   * clusters, not single smooth balls (the 0.0016 first cut put under one
   * noise period across a volume ⇒ balloon shapes in the mesh bed).
   */
  noiseScale?: number;
  /** Weather-map UV per metre for CPU placement. Default 0.0001 (10 km tile). */
  weatherScale?: number;
  /** Extinction (1/m). Default 0.04 — softer than the deck's 0.05 so the
   *  smaller volumes keep translucent edges instead of chalk-flat interiors. */
  extinction?: number;
  /** Primary march steps per fragment. Default 20. */
  steps?: number;
  /** Sun-cone samples per non-zero primary sample. Default 2. */
  sunSteps?: number;
  /** Fraction of fieldRadius where the distance fade starts. Default 0.72. */
  fadeStartRatio?: number;
  /** Wind (m/s, pattern scroll) + time scale — inherited from the module. */
  windX?: number;
  windZ?: number;
  windTimeScale?: number;
  /** Net-zero shape evolution speed (m/s). Default 1.5 (matches the deck). */
  evolveSpeed?: number;
  /**
   * CONDENSATION (2026-09-10): a cloud born INSIDE the visible field (a
   * coverage rise, a regime change) grows over this many seconds — the
   * subtractive gate starts high so only the densest cores survive as wisps,
   * then relaxes until the turrets fill in. Real cumulus condense over
   * minutes; a 3 s alpha fade read as "the mesh just forms". Births at the
   * field's far edge keep the short fade (they cross-fade with the impostor
   * layer there). Default 40.
   */
  growSeconds?: number;
  /**
   * March-step multiplier for the field's farthest clouds (linear from 1 at
   * 0.35·fieldRadius to this at fieldRadius): a cloud 4 km out spans a few
   * dozen pixels and needs a third of the samples. Default 2.5.
   */
  lodFarStep?: number;
  /**
   * The far LOD (`CloudImpostorLayer`): instanced sprites carrying the SAME
   * weather lattice from the field's fade band out to `farRadius`, cross-
   * faded with the volumetric clouds. `false` disables it; defaults apply
   * when omitted. Consumed by CloudModule.
   */
  impostors?: CloudImpostorLayerConfig | false;
}

/** Far-LOD sprite layer configuration (see CloudImpostorLayer). */
export interface CloudImpostorLayerConfig {
  /** Sprites on the field's 700 m lattice out to here (m). Default 16000. */
  midRadius?: number;
  /** Coarser far lattice (cellSize·farCellScale) out to here (m). Default 60000. */
  farRadius?: number;
  /** Far-lattice cell multiplier. Default 4. */
  farCellScale?: number;
  /** Instance pool. Default 1600. */
  maxSprites?: number;
}

export interface CloudMeshFieldOptions {
  config?: CloudMeshFieldConfig;
  baseNoise: THREE.Data3DTexture;
  detailNoise: THREE.Data3DTexture;
  weatherMap: THREE.DataTexture;
}

// ─── Pure CPU helpers (unit-tested) ─────────────────────────────────────────

/** Deterministic 2D→1D cell hash in [0, 1). */
export function hashCell(cx: number, cz: number, salt: number): number {
  let h = (cx * 374761393 + cz * 668265263 + salt * 2246822519) | 0;
  h = Math.imul(h ^ (h >>> 13), 1274126177);
  h ^= h >>> 16;
  return (h >>> 0) / 4294967296;
}

/**
 * Global-coverage → per-cell weather.r spawn threshold. `overcast` is the FIELD's global coverage (not the layer's):
 * THE OVERCAST FLOOR (2026-10-06) — the 0.25 floor kept a cell whose weather-map coverage sat under 0.25 cloudless at
 * ANY cover, so a pinned overcast left holes wherever the map was low (Atlas's hub city stood 1.6 km from its nearest
 * cloud at 0.975). The floor fades to 0 as the global cover goes 0.85 → 0.95, so an overcast is an overcast everywhere;
 * a broken sky (the layer multipliers push a layer's own coverage past 0.6) keeps its gaps.
 */
export function coverageThreshold(coverage: number, overcast = 0): number {
  const t = Math.max(0, Math.min(1, (overcast - 0.85) / 0.1));
  const floor = 0.25 * (1 - t * t * (3 - 2 * t));
  return Math.max(floor, Math.min(0.95, 1.1 - coverage * 1.4));
}

/**
 * Bilinear sample of the weather texture's R (coverage) / G (type) channels
 * at absolute world (x, z), RepeatWrapping. `data` is the RGBA8 backing array
 * of the generated weather DataTexture.
 */
export function sampleWeatherCpu(
  data: Uint8Array, size: number, x: number, z: number, weatherScale: number,
): { r: number; g: number } {
  const u = ((x * weatherScale) % 1 + 1) % 1;
  const v = ((z * weatherScale) % 1 + 1) % 1;
  const fx = u * size - 0.5;
  const fz = v * size - 0.5;
  const x0 = Math.floor(fx), z0 = Math.floor(fz);
  const tx = fx - x0, tz = fz - z0;
  const wrap = (i: number) => ((i % size) + size) % size;
  const px = (ix: number, iz: number, ch: number) =>
    data[(wrap(iz) * size + wrap(ix)) * 4 + ch] / 255;
  const lerp = (a: number, b: number, t: number) => a + (b - a) * t;
  const r = lerp(
    lerp(px(x0, z0, 0), px(x0 + 1, z0, 0), tx),
    lerp(px(x0, z0 + 1, 0), px(x0 + 1, z0 + 1, 0), tx), tz);
  const g = lerp(
    lerp(px(x0, z0, 1), px(x0 + 1, z0, 1), tx),
    lerp(px(x0, z0 + 1, 1), px(x0 + 1, z0 + 1, 1), tx), tz);
  return { r, g };
}

export interface DesiredCell {
  key: string;
  cx: number;
  cz: number;
  dist: number;
}

// ─── Cloud genera (étage archetypes) ────────────────────────────────────────

/**
 * `congestus` and `fractus` joined on 2026-10-07 (the cloud-realism work): the heap family is humilis → mediocris (the
 * `cumulus` archetype, by `type01` / the layer's `vigor`) → congestus (a tower taller than it is wide, cauliflower all
 * the way up), and the ragged scud that hangs under a rain deck. The layer genera (stratocumulus, altocumulus, cirrus)
 * are kept for callers that draw them as volumes; a sky that draws its layers as sheets leaves them out of its formation.
 */
export type CloudLayerKind = 'cumulus' | 'congestus' | 'cumulonimbus' | 'fractus' | 'stratocumulus' | 'altocumulus' | 'cirrus';

/**
 * Per-genus morphology + shading parameters, researched against the WMO
 * étage classification:
 *   cumulus       (low)  — cellular, flat base, rounded vertical tops
 *   stratocumulus (low)  — clumped layer of joined lumps, thick/thin, gray
 *   altocumulus   (mid)  — patches of MANY small separate globular cloudlets
 *   cirrus        (high) — hair-like translucent streaks aligned to the wind
 */
export interface CloudArchetype {
  radiusMin: number;
  radiusMax: number;
  /** Ellipsoid flattening range (lerped by type01 = weather.g). */
  flattenMin: number;
  flattenMax: number;
  /** Density multiplier (stratocumulus > 1 ⇒ self-shadowed gray). */
  density: number;
  /** Fringe-erosion multiplier (cirrus high ⇒ wispy shreds). */
  erode: number;
  /** Wind-aligned noise anisotropy (0 = isotropic; cirrus ~3 ⇒ streaks). */
  stretch: number;
  /** Streaming cell-size multiplier (<1 ⇒ denser packing / more, smaller). */
  cellScale: number;
  /** A giant the far layer alone draws (never a pooled volume; never dropped by the sprite cap). */
  farOnly?: boolean;
  /** Base-altitude jitter (m). */
  baseJitter: number;
}

export const CLOUD_ARCHETYPES: Record<CloudLayerKind, CloudArchetype> = {
  cumulus: {
    // flatten 0.45–0.85 → 0.34–0.95 (2026-10-07): a humilis is a third as tall as it is wide ("flattened", WMO) and a
    // strong mediocris as tall as wide; 0.45–0.85 made every cloud the same middling dome.
    radiusMin: 220, radiusMax: 560, flattenMin: 0.34, flattenMax: 0.95,
    // baseJitter 40→14 (2026-08-27): real cumulus bases all sit AT the lifting
    // condensation level — a shared flat ceiling with tops of varied height is
    // THE cumulus signature, and the old ±40 m scatter erased it.
    density: 1.0, erode: 1.0, stretch: 0, cellScale: 1.0, baseJitter: 14,
  },
  congestus: {
    // A towering cumulus, read off the photographs: a MASSIF 1.3–2 km across and 0.8–1.7 km tall (height = 2·radius·
    // flatten: 0.6–0.85 of its width — 0.95–1.5 drew needles), sharp cauliflower, a dark base; few and far apart.
    radiusMin: 650, radiusMax: 1000, flattenMin: 0.6, flattenMax: 0.85,
    density: 1.15, erode: 0.8, stretch: 0, cellScale: 2.4, baseJitter: 14,
  },
  cumulonimbus: {
    // The storm on the horizon: an anvil 5–6.6 km across (0.55 of the radius each way) over a trunk half as wide,
    // 4.5–7 km tall (height = 2·radius·flatten). FAR ONLY — it is drawn by the far layer from about three bounding radii out (a
    // giant's card cannot hold a camera inside it); nearer, the storm is the deck above.
    radiusMin: 4500, radiusMax: 6000, flattenMin: 0.5, flattenMax: 0.58,
    density: 1.25, erode: 0.6, stretch: 0, cellScale: 10, baseJitter: 20, farOnly: true,
  },
  fractus: {
    // Scud (fractus / pannus): small, low, ragged shreds with no flat base to speak of — thin and heavily eroded.
    radiusMin: 90, radiusMax: 220, flattenMin: 0.3, flattenMax: 0.55,
    density: 0.6, erode: 1.7, stretch: 0.6, cellScale: 0.6, baseJitter: 60,
  },
  stratocumulus: {
    radiusMin: 420, radiusMax: 720, flattenMin: 0.26, flattenMax: 0.4,
    density: 1.35, erode: 0.65, stretch: 0, cellScale: 0.85, baseJitter: 12,
  },
  altocumulus: {
    radiusMin: 110, radiusMax: 200, flattenMin: 0.3, flattenMax: 0.45,
    density: 0.9, erode: 0.85, stretch: 0, cellScale: 0.42, baseJitter: 60,
  },
  cirrus: {
    radiusMin: 520, radiusMax: 950, flattenMin: 0.06, flattenMax: 0.1,
    density: 0.26, erode: 1.5, stretch: 3, cellScale: 1.6, baseJitter: 120,
  },
};

/** One active étage of the sky: which genus, where, how much, what pool share. */
export interface CloudLayerSpec {
  kind: CloudLayerKind;
  /** The layer's condensation level (m) — every base in the layer aligns here. */
  baseAltitude: number;
  /** Multiplier on the global coverage for this layer's spawn threshold. */
  coverage: number;
  /** Fraction of the mesh pool this layer may claim (shares are normalized). */
  share: number;
  /**
   * How vigorous the layer's convection is, 0..1 (optional): it biases every cloud's species inside the genus — 0.2 a
   * fair calm day of flat humilis, 0.8 an unstable afternoon of tall mediocris. Absent = the weather map's type alone
   * (the old behaviour, bit for bit).
   */
  vigor?: number;
}

export interface CloudFormation {
  layers: CloudLayerSpec[];
}

/**
 * Weather → formation (unit-tested), following the observed frontal
 * sequences: fair skies carry scattered cumulus + sparse cirrus; humid
 * pre-frontal skies add altocumulus patches and thickening cirrus; active
 * precipitation flips the low deck to gray joined stratocumulus with an
 * altocumulus veil and hides the high étage entirely.
 *
 * `coverage` (0..1) selects the REGIME; each layer's `coverage` field is a
 * MULTIPLIER on the field's global coverage (the field computes
 * layerCoverage = global · multiplier — passing absolutes here would apply
 * the sky knob twice). `precip01` = precipitation intensity (0 = dry).
 * Étage base altitudes are the caller's (game-scale).
 */
export function formationForWeather(
  coverage: number, precip01: number,
  alts: { low: number; mid: number; high: number },
): CloudFormation {
  const cov = Math.max(0, Math.min(1, coverage));
  const wet = Math.max(0, Math.min(1, precip01));
  if (wet > 0.05) {
    // Rain/snow: nimbo-like joined gray deck; no visible high étage.
    return {
      layers: [
        { kind: 'stratocumulus', baseAltitude: alts.low * 0.9, coverage: 1.5, share: 0.62 },
        { kind: 'altocumulus', baseAltitude: alts.mid, coverage: 0.7, share: 0.38 },
      ],
    };
  }
  if (cov > 0.55) {
    // Humid / pre-frontal: mediocris + altocumulus patches + thick cirrus.
    return {
      layers: [
        { kind: 'cumulus', baseAltitude: alts.low, coverage: 1, share: 0.5 },
        { kind: 'altocumulus', baseAltitude: alts.mid, coverage: 0.8, share: 0.3 },
        { kind: 'cirrus', baseAltitude: alts.high, coverage: 0.9, share: 0.2 },
      ],
    };
  }
  // Fair weather: scattered cumulus, a few altocumulus, sparse cirrus.
  return {
    layers: [
      { kind: 'cumulus', baseAltitude: alts.low, coverage: 1, share: 0.6 },
      { kind: 'altocumulus', baseAltitude: alts.mid, coverage: 0.5, share: 0.15 },
      { kind: 'cirrus', baseAltitude: alts.high, coverage: 0.75, share: 0.25 },
    ],
  };
}

export interface CloudShape {
  /** Horizontal semi-axis (m). */
  radius: number;
  /** Vertical/horizontal ratio (ellipsoid flattening). */
  flatten: number;
  /** Vertical semi-axis (m) = radius·flatten. */
  ry: number;
  /** Ellipsoid center altitude (m) — base + ry, so the BOTTOM sits at base. */
  centerY: number;
  /** This cloud's base altitude (m). */
  baseY: number;
  /** Dome height (m) over the base = 2·ry: the mesh is the UPPER HALF of the
   *  ellipsoid with the flat base through its equator (2026-09-10). */
  height: number;
}

/**
 * Genus morphology (unit-tested). `type01` = weather.g lerps size and
 * flattening inside the genus archetype (cumulus: humilis → mediocris/
 * congestus). All bases in a layer sit at its condensation level (±jitter)
 * — tops vary.
 */
export function computeCloudShape(
  sizeHash01: number, type01: number, jitterHash01: number,
  cfg: { baseAltitude: number },
  arch: CloudArchetype = CLOUD_ARCHETYPES.cumulus,
): CloudShape {
  // Species-like size spread (2026-08-27 variety fix): quadratic bias means
  // many modest humilis and the occasional towering congestus — the old
  // `0.3 + 0.5·h + 0.2·t` collapsed every cloud into a narrow middle band.
  const radius = arch.radiusMin +
    (arch.radiusMax - arch.radiusMin) * Math.min(1, 0.16 + 0.84 * sizeHash01 * sizeHash01 + 0.16 * type01);
  // Flatten keeps its type01 monotonic trend but gains a per-cloud wobble so
  // neighbours under the same weather no longer share one silhouette.
  const flatten = arch.flattenMin + (arch.flattenMax - arch.flattenMin) *
    Math.max(0, Math.min(1, type01 * 0.75 + (sizeHash01 - 0.5) * 0.35 + 0.08));
  const ry = radius * flatten;
  const baseY = cfg.baseAltitude + (jitterHash01 - 0.5) * 2 * arch.baseJitter;
  return { radius, flatten, ry, centerY: baseY + ry, baseY, height: ry * 2 };
}

/** Everything one cloud cell derives from its hashes + the weather — shared by the
 *  volumetric field and the impostor layer so a cloud keeps its place, size,
 *  character and turret layout across the LOD hand-off. Pure. */
export interface CloudCell {
  absX: number; absZ: number;
  shape: CloudShape;
  /** x = density mul, y = erosion mul, z = wind stretch. */
  params: { x: number; y: number; z: number };
  /** Noise seed offset (m) — whole noise tiles apart per cloud. */
  seed: { x: number; y: number; z: number };
  lobes: CloudLobe[];
  /** weather.g at the cell (the type). */
  type01: number;
}

export function deriveCloudCell(
  cx: number, cz: number, cellSize: number, spec: CloudLayerSpec, arch: CloudArchetype,
  weather: { data: Uint8Array; size: number }, weatherScale: number,
): CloudCell {
  const h1 = hashCell(cx, cz, 1), h2 = hashCell(cx, cz, 2), h3 = hashCell(cx, cz, 3);
  const h4 = hashCell(cx, cz, 4), h5 = hashCell(cx, cz, 5), h6 = hashCell(cx, cz, 6);
  const w = sampleWeatherCpu(weather.data, weather.size, (cx + 0.5) * cellSize, (cz + 0.5) * cellSize, weatherScale);
  const absX = (cx + 0.5 + (h1 - 0.5) * 0.7) * cellSize;
  const absZ = (cz + 0.5 + (h2 - 0.5) * 0.7) * cellSize;
  // THE LAYER'S VIGOR: the weather map's type (humilis … congestus, per cell) shifted by how unstable the day is.
  const type01 = spec.vigor === undefined ? w.g : Math.max(0, Math.min(1, w.g * 0.6 + spec.vigor * 0.8 - 0.2));
  const shape = computeCloudShape(h3, type01, h4, { baseAltitude: spec.baseAltitude }, arch);
  return {
    absX, absZ, shape,
    // Per-cloud CHARACTER (the "they all look the same" fix, 2026-08-27):
    // density and erosion each get a ±~30% personal spread around the genus.
    params: { x: arch.density * (0.78 + 0.5 * h5), y: arch.erode * (0.7 + 0.65 * h6), z: arch.stretch },
    // Seed jumps WHOLE NOISE TILES (the base texture tiles every ~263 m):
    // ×64 moved a cloud a quarter-tile — every cloud a window onto one lattice.
    seed: { x: h4 * 8192, y: h1 * 4096, z: h2 * 8192 },
    lobes: cloudLobes([h1, h2, h3, h4, h5, h6], type01, spec.kind),
    type01,
  };
}

/**
 * The set of cells that should hold a cloud: every cell within `fieldRadius`
 * of (camX, camZ) whose weather coverage passes `threshold`, nearest-first,
 * capped at `maxClouds`. Deterministic for a given camera position — clouds
 * persist when the player leaves and returns.
 */
export function computeDesiredCells(
  camX: number, camZ: number, coverage: number,
  opts: {
    fieldRadius: number; cellSize: number; maxClouds: number; weatherScale: number;
    /** Weather-sampling offset (m) — decorrelates étages so layers don't stack. */
    weatherOffset?: { x: number; z: number };
    /** 70/30 near-first bucketing (walk-parallax legibility) — low étage only. */
    nearBias?: boolean;
    /** The field's GLOBAL coverage (coverageThreshold's overcast floor); absent = 0 (the old floor). */
    overcast?: number;
    /** Key prefix so per-layer cell keys never collide in the shared pool. */
    keyPrefix?: string;
    /** Cells CURRENTLY holding a cloud — they pass a relaxed threshold and a
     *  slightly larger radius (hysteresis: a visible cloud never flickers off
     *  because the lerped coverage grazed the cut line). */
    sticky?: Set<string>;
  },
  weather: { data: Uint8Array; size: number },
): DesiredCell[] {
  const { fieldRadius, cellSize, maxClouds, weatherScale } = opts;
  const off = opts.weatherOffset ?? { x: 0, z: 0 };
  const prefix = opts.keyPrefix ?? '';
  const threshold = coverageThreshold(coverage, opts.overcast ?? 0);
  const ccx = Math.floor(camX / cellSize);
  const ccz = Math.floor(camZ / cellSize);
  const range = Math.ceil(fieldRadius / cellSize);
  const out: DesiredCell[] = [];
  for (let dx = -range; dx <= range; dx++) {
    for (let dz = -range; dz <= range; dz++) {
      const cx = ccx + dx, cz = ccz + dz;
      const wx = (cx + 0.5) * cellSize, wz = (cz + 0.5) * cellSize;
      const dist = Math.hypot(wx - camX, wz - camZ);
      const key = `${prefix}${cx},${cz}`;
      const held = opts.sticky?.has(key) ?? false;
      if (dist > fieldRadius * (held ? 1.06 : 1)) continue;
      const w = sampleWeatherCpu(weather.data, weather.size, wx + off.x, wz + off.z, weatherScale);
      if (w.r < threshold - (held ? 0.05 : 0)) continue;
      out.push({ key, cx, cz, dist });
    }
  }
  out.sort((a, b) => a.dist - b.dist);
  if (out.length <= maxClouds) return out;
  // STABLE over-budget selection: a per-cell hash priority. The old positional
  // stride (`out[i·len/max]`) re-rolled the ENTIRE layer whenever the candidate
  // count changed by one (every weather tick near the threshold) — a major
  // co-cause of the turn-the-camera popping. Hash priority means an appearing/
  // disappearing candidate displaces at most one selection, and held cells get
  // a priority bonus so selection churn favors keeping what's visible.
  const pick = (cands: DesiredCell[], n: number): DesiredCell[] =>
    cands
      .map((c) => ({ c, p: hashCell(c.cx, c.cz, 7) + (opts.sticky?.has(c.key) ? 0.35 : 0) }))
      .sort((a, b) => b.p - a.p)
      .slice(0, n)
      .map((x) => x.c)
      .sort((a, b) => a.dist - b.dist); // callers + tests expect nearest-first order
  if (opts.nearBias === false) {
    // Mid/high étages: even sky fill via the stable hash spread.
    return pick(out, maxClouds);
  }
  // Over budget: BUCKET the pool 70/30 near/far. Nearest-first alone spends
  // the whole pool within ~4 km and leaves the horizon empty (mesh bed
  // M6/M7); a pure distance stride thins the near field until walk-speed
  // parallax disappears (the 2026-08-19 regression: nearParallax MAD 10 →
  // 1.6). Near bucket = nearest-first within 0.45·fieldRadius (parallax
  // legibility lives here); far bucket = stride-spread over the rest
  // (horizon fill). A short near bucket hands its slots to the far bucket.
  const nearBudget = Math.ceil(maxClouds * 0.7);
  const nearEdge = opts.fieldRadius * 0.45;
  const near = out.filter((d) => d.dist <= nearEdge).slice(0, nearBudget);
  const farCands = out.filter((d) => d.dist > nearEdge);
  const farBudget = maxClouds - near.length;
  const far = pick(farCands, Math.min(farBudget, farCands.length));
  return [...near, ...far];
}

// ─── The field ──────────────────────────────────────────────────────────────

/**
 * THE UPLIGHT PROVIDER (the night-sky realism wave, 2026-10-06): the light the GROUND sends up at an ABSOLUTE world point
 * (x, z) — a city's lamps and lit windows reflected back down by the cloud base above it. Returns an RGB in the CALLER's
 * units (Atlas: the artificial sky's zenith ratio × the glow's tint); the field multiplies it by `uplightScale` (scene
 * radiance per unit) in the shader. Evaluated once per cloud when it is assigned a cell (a cloud never moves: its cell
 * is fixed, the wind scrolls the noise) and on `refreshUplight()`. Null / NaN / negative = no uplight.
 */
export type CloudUplightProvider = (absX: number, absZ: number) => { r: number; g: number; b: number } | null;

interface MeshCloudUniforms {
  center: ReturnType<typeof uniform>;
  radius: ReturnType<typeof uniform>;
  /** Retirement timestamp (uTime units); 1e9 = alive. Drives the fade-OUT —
   *  despawn used to be an instant `visible = false`, a one-frame pop. */
  death: ReturnType<typeof uniform>;
  /** Vertical/horizontal semi-axis ratio (ellipsoid flattening, ≤1). */
  flatten: ReturnType<typeof uniform>;
  /** Genus params: x = density mul, y = fringe-erosion mul, z = wind-aligned
   *  noise stretch (0 = isotropic; cirrus ~3 = hair-like streaks). */
  params: ReturnType<typeof uniform>;
  seed: ReturnType<typeof uniform>;
  birth: ReturnType<typeof uniform>;
  /** Condensation time (s): the subtractive gate relaxes from +0.6 to 0 over it. */
  grow: ReturnType<typeof uniform>;
  /** March-step multiplier by distance (1 near → lodFarStep at the field edge). */
  lod: ReturnType<typeof uniform>;
  /** The turret cluster (shape space): MAX_LOBES × (x, y, z, r). */
  lobes: ReturnType<typeof uniformArray>;
  /** The ground's light under THIS cloud (the provider's units; × `uplightScale`) — the uplight (2026-10-06). */
  uplight: ReturnType<typeof uniform>;
}

interface PoolEntry {
  mesh: THREE.Mesh;
  u: MeshCloudUniforms;
  cellKey: string | null;
  /** While > 0: retired but still fading out until this _elapsed time. */
  dyingUntil: number;
}

export class CloudMeshField {
  readonly group = new THREE.Group();
  /** Last quantized coverage bucket — the re-stream latch key (see setCoverage). */
  private _covBucket = -1;
  /** Cell keys currently holding a live cloud (the hysteresis sticky set). */
  private readonly _heldKeys = new Set<string>();
  /** Seconds a retired cloud takes to fade out (mirrors the 3 s birth ramp). */
  static readonly FADE_OUT_S = 2.5;
  /** Shared uniform nodes — single-point updates reach every mesh material.
   *  The night-ramp/weather drivers write these (same names as CloudUniforms). */
  readonly uniforms: {
    sunDirection: ReturnType<typeof uniform>;
    sunColor: ReturnType<typeof uniform>;
    sunIlluminance: ReturnType<typeof uniform>;
    ambientBottom: ReturnType<typeof uniform>;
    ambientTop: ReturnType<typeof uniform>;
    coverage: ReturnType<typeof uniform>;
    densityScale: ReturnType<typeof uniform>;
    // ── Cloud lighting v2 (2026-09-25, Atlas realism §8 R6a) — neutral defaults keep the old look. ──
    /** Hue of the sky light on the tops / the ground light on the bases (× the scalar ambientTop / Bottom). */
    ambientTopColor: ReturnType<typeof uniform>;
    ambientBottomColor: ReturnType<typeof uniform>;
    /** The droplets' ALBEDO — the biome/weather tint multiplies the scattered light (sun AND sky), never the sun's colour. */
    albedo: ReturnType<typeof uniform>;
    /** Wrenninge multiple-scattering octaves' weight (0 = single scattering only, the old model; 1 = the full 3-octave sum). */
    multiScatter: ReturnType<typeof uniform>;
    /**
     * THE LIGHTNING HOOK (for the fire/VFX agent's flash lobe): an emission term
     * flashIntensity · e^(−|p − flashPos| / flashRadius) added per march step,
     * flashPos in ABSOLUTE world metres. Intensity 0 (the default) = nothing.
     */
    uFlashPos: ReturnType<typeof uniform>;
    uFlashIntensity: ReturnType<typeof uniform>;
    uFlashRadius: ReturnType<typeof uniform>;
    /**
     * THE UPLIGHT (the night-sky realism wave, 2026-10-06): scene radiance per provider unit (0 = off — the old look)
     * and the base → top decay, in e-folds over the dome height (the light enters the flat BASE and diffuses up through
     * an optically thick volume: the underside glows, the tops stay dark — a lit city's overcast seen from below).
     */
    uplightScale: ReturnType<typeof uniform>;
    uplightFalloff: ReturnType<typeof uniform>;
  };

  private readonly _cfg: Required<Omit<CloudMeshFieldConfig, never>>;
  private readonly _baseNoise: THREE.Data3DTexture;
  private readonly _detailNoise: THREE.Data3DTexture;
  private readonly _weatherData: Uint8Array;
  private readonly _weatherSize: number;
  private readonly _pool: PoolEntry[] = [];
  private _enabled = true;
  private _elapsed = 0;
  private _windX = 0;
  private _windZ = 0;
  private _evolve = 0;
  private _lastCellX = Number.NaN;
  /**
   * The last re-stream wanted more cells than the pool had free meshes
   * (dying meshes are excluded — they keep rendering their fade-out). WHY:
   * a re-stream only ran on a cell crossing, so after a teleport (all 40
   * dying at once) the sky stayed EMPTY until the camera crossed the next
   * 700 m cell, and in steady walking the entering column was always one
   * crossing behind. A park (fade done) now re-arms the latch (2026-09-12).
   */
  private _starved = false;
  private _lastCellZ = Number.NaN;
  private _coverage = 0.4;
  // Shared shader-side offsets (single node objects shared by all materials).
  private readonly _uWind: ReturnType<typeof uniform>;
  private readonly _uEvolve: ReturnType<typeof uniform>;
  private readonly _uWorldOffset: ReturnType<typeof uniform>;
  private readonly _uTime: ReturnType<typeof uniform>;
  private readonly _uFadeStart: ReturnType<typeof uniform>;
  private readonly _uFadeEnd: ReturnType<typeof uniform>;
  private readonly _uNoiseScale: ReturnType<typeof uniform>;
  private readonly _uExtinction: ReturnType<typeof uniform>;
  /** Diagnostic view (0 = off, 1 = chord-midpoint rNorm/baseNoise/boundGate
   *  as RGB) — flip via `setDebugView(true)` from a probe/console. */
  private readonly _uDebug: ReturnType<typeof uniform>;
  private readonly _uWindDir!: ReturnType<typeof uniform>;
  /** Sun-cone samples per march step — a uniform bound so a tier can raise it live (setSunSteps). */
  private readonly _uSunSteps: ReturnType<typeof uniform>;
  /** Active étages (see setFormation / formationForWeather). */
  private _formation!: CloudLayerSpec[];
  /** False until the first re-stream — births before it are the initial fill, not condensation. */
  private _everStreamed = false;

  constructor(opts: CloudMeshFieldOptions) {
    const c = opts.config ?? {};
    this._cfg = {
      fieldRadius: c.fieldRadius ?? 9000,
      cellSize: c.cellSize ?? 700,
      maxClouds: c.maxClouds ?? 40,
      baseAltitude: c.baseAltitude ?? 750,
      baseJitter: c.baseJitter ?? 40,
      radiusMin: c.radiusMin ?? 220,
      radiusMax: c.radiusMax ?? 520,
      noiseScale: c.noiseScale ?? 0.0038,
      weatherScale: c.weatherScale ?? 0.0001,
      extinction: c.extinction ?? 0.04,
      steps: c.steps ?? 20,
      sunSteps: c.sunSteps ?? 2,
      fadeStartRatio: c.fadeStartRatio ?? 0.72,
      windX: c.windX ?? 0,
      windZ: c.windZ ?? 0,
      windTimeScale: c.windTimeScale ?? 1,
      evolveSpeed: c.evolveSpeed ?? 1.5,
      growSeconds: c.growSeconds ?? 40,
      lodFarStep: c.lodFarStep ?? 2.5,
      impostors: c.impostors ?? {},
    };
    this._baseNoise = opts.baseNoise;
    this._detailNoise = opts.detailNoise;
    const wImg = opts.weatherMap.image as { data: Uint8Array; width: number };
    this._weatherData = wImg.data;
    this._weatherSize = wImg.width;

    this.uniforms = {
      sunDirection: uniform(vec3(0, 1, 0)),
      sunColor: uniform(vec3(1, 1, 1)),
      sunIlluminance: uniform(5),
      ambientBottom: uniform(0.3),
      ambientTop: uniform(1.0),
      coverage: uniform(0.4),
      densityScale: uniform(1),
      ambientTopColor: uniform(vec3(1, 1, 1)),
      ambientBottomColor: uniform(vec3(0.8, 0.9, 1.1)),
      albedo: uniform(vec3(1, 1, 1)),
      multiScatter: uniform(0),
      uFlashPos: uniform(vec3(0, -1e6, 0)),
      uFlashIntensity: uniform(0),
      uFlashRadius: uniform(900),
      uplightScale: uniform(0),
      uplightFalloff: uniform(3),
    };
    this._uSunSteps = uniform(Math.max(1, Math.min(8, this._cfg.sunSteps)), 'int');
    this._uWind = uniform(vec3(0, 0, 0));
    this._uEvolve = uniform(vec3(0, 0, 0));
    this._uWorldOffset = uniform(vec3(0, 0, 0));
    this._uTime = uniform(0);
    this._uFadeStart = uniform(this._cfg.fieldRadius * this._cfg.fadeStartRatio);
    this._uFadeEnd = uniform(this._cfg.fieldRadius);
    this._uNoiseScale = uniform(this._cfg.noiseScale);
    this._uExtinction = uniform(this._cfg.extinction);
    this._uDebug = uniform(0);
    // Wind direction (unit XZ) for cirrus streak alignment — real cirrus
    // hair aligns with the upper-level wind.
    const wl = Math.hypot(this._cfg.windX, this._cfg.windZ);
    this._uWindDir = uniform(new THREE.Vector2(
      wl > 1e-6 ? this._cfg.windX / wl : 1, wl > 1e-6 ? this._cfg.windZ / wl : 0));
    // Default formation: a single cumulus étage at the configured base.
    this._formation = [
      { kind: 'cumulus', baseAltitude: this._cfg.baseAltitude, coverage: 1, share: 1 },
    ];

    // Pooled meshes — one closed unit HEMISPHERE (dome + base cap) + one
    // material each. The flat base runs through the ball's equator (2026-09-10):
    // a bound whose bottom is a point held no flat-based turret off its axis,
    // so bases were a narrow cap and every outline a lozenge. All materials
    // share the global uniform nodes, so per-frame updates are single-point.
    const dome = new THREE.SphereGeometry(1, 24, 12, 0, Math.PI * 2, 0, Math.PI / 2);
    const cap = new THREE.CircleGeometry(1, 24);
    cap.rotateX(Math.PI / 2); // faces −y: outward for the underside
    const geo = mergeGeometries([dome, cap], false) ?? dome;
    dome.dispose(); cap.dispose();
    // Frustum grace margin: the analytic bound is EXACT, so a cloud used to
    // vanish the very frame its sphere left the view — right at the screen
    // edge, which read as turn-dependent popping. Shared geometry: one
    // inflate covers the whole pool.
    geo.computeBoundingSphere();
    if (geo.boundingSphere) geo.boundingSphere.radius = 1.15;
    for (let i = 0; i < this._cfg.maxClouds; i++) {
      const u: MeshCloudUniforms = {
        center: uniform(new THREE.Vector3(0, -10000, 0)),
        radius: uniform(300),
        flatten: uniform(0.6),
        params: uniform(new THREE.Vector3(1, 1, 0)),
        seed: uniform(new THREE.Vector3()),
        birth: uniform(-1000),
        death: uniform(1e9),
        grow: uniform(3),
        lod: uniform(1),
        lobes: uniformArray(Array.from({ length: MAX_LOBES }, () => new THREE.Vector4(0, -10, 0, 0)), 'vec4'),
        uplight: uniform(new THREE.Vector3(0, 0, 0)),
      };
      const material = this._buildMaterial(u);
      const mesh = new THREE.Mesh(geo, material);
      mesh.frustumCulled = true;
      mesh.visible = false;
      mesh.renderOrder = 10; // after opaques; among transparents three sorts by center depth
      this.group.add(mesh);
      this._pool.push({ mesh, u, cellKey: null, dyingUntil: 0 });
    }
    this.group.name = 'CloudMeshField';
  }

  get enabled(): boolean { return this._enabled; }

  /** Number of pool meshes currently assigned to a cell (debug/verify). */
  get activeCount(): number {
    let n = 0;
    for (const e of this._pool) if (e.cellKey) n++;
    return n;
  }

  setEnabled(on: boolean): void {
    this._enabled = on;
    this.group.visible = on;
  }

  setCoverage(v: number): void {
    this._coverage = Math.max(0, Math.min(1, v));
    this.uniforms.coverage.value = this._coverage;
    // Re-stream only when coverage crosses a QUANTIZED bucket. The runtime
    // drives this every frame with a continuously-lerped value; NaN-ing the
    // latch unconditionally turned the designed cell-boundary re-stream into
    // a 60 Hz re-roll — the "clouds pop when I turn the camera" field bug
    // (2026-08-27): every frame could silently retire a visible cloud and
    // seed another. The shader uniform still tracks the smooth value.
    const bucket = Math.round(this._coverage * 25); // 0.04-wide steps
    if (bucket !== this._covBucket) {
      this._covBucket = bucket;
      this._lastCellX = Number.NaN;
    }
  }

  /**
   * Set the active étages (see `formationForWeather` for the weather-driven
   * default). Layers re-stream on the next update; per-genus morphology and
   * shading come from `CLOUD_ARCHETYPES`.
   */
  setFormation(f: CloudFormation): void {
    if (!f.layers.length) return;
    this._formation = f.layers.map((l) => ({ ...l }));
    this._lastCellX = Number.NaN;
  }

  get formation(): CloudLayerSpec[] { return this._formation; }

  setSunDirection(dir: { x: number; y: number; z: number }): void {
    (this.uniforms.sunDirection.value as THREE.Vector3).set(dir.x, dir.y, dir.z);
  }

  setSunColor(col: { r: number; g: number; b: number }): void {
    (this.uniforms.sunColor.value as THREE.Vector3).set(col.r, col.g, col.b);
  }

  setDensityScale(v: number): void {
    this.uniforms.densityScale.value = Math.max(0, v);
  }

  // ── Cloud lighting v2 (2026-09-25) setters ──
  /** The droplets' albedo (the biome / weather tint). */
  setAlbedo(col: { r: number; g: number; b: number }): void {
    (this.uniforms.albedo.value as THREE.Vector3).set(col.r, col.g, col.b);
  }
  /** Sky-light hues on the tops and the bases (the scalars ambientTop / ambientBottom stay the levels). */
  setAmbientColors(top: { r: number; g: number; b: number }, bottom: { r: number; g: number; b: number }): void {
    (this.uniforms.ambientTopColor.value as THREE.Vector3).set(top.r, top.g, top.b);
    (this.uniforms.ambientBottomColor.value as THREE.Vector3).set(bottom.r, bottom.g, bottom.b);
  }
  /** Multiple-scattering octave weight 0..1. */
  setMultiScatter(w: number): void { this.uniforms.multiScatter.value = Math.max(0, Math.min(1, w)); }
  /** Sun-cone samples per step (1..8; the tier's quality lever). */
  setSunSteps(n: number): void { this._uSunSteps.value = Math.max(1, Math.min(8, Math.round(n))); }
  get sunSteps(): number { return this._uSunSteps.value as number; }
  /** The lightning hook: a flash at an ABSOLUTE point (intensity 0 = off). */
  setFlash(posAbs: { x: number; y: number; z: number }, intensity: number, radius = 900): void {
    (this.uniforms.uFlashPos.value as THREE.Vector3).set(posAbs.x, posAbs.y, posAbs.z);
    this.uniforms.uFlashIntensity.value = Math.max(0, intensity);
    this.uniforms.uFlashRadius.value = Math.max(1, radius);
  }

  // ── THE UPLIGHT (the night-sky realism wave, 2026-10-06) ──
  private _uplightProvider: CloudUplightProvider | null = null;
  /** Bumped when the provider changes — the impostor layer re-streams its sprites on it. */
  private _uplightGen = 0;
  /** Scene radiance per provider unit (≤ 0 = off) and, optionally, the base → top decay (e-folds over the dome). */
  setUplight(scale: number, falloff?: number): void {
    this.uniforms.uplightScale.value = Number.isFinite(scale) ? Math.max(0, scale) : 0;
    if (falloff !== undefined && Number.isFinite(falloff)) this.uniforms.uplightFalloff.value = Math.max(0, falloff);
  }
  /** Install (or clear) the ground-light provider; every live cloud is re-evaluated and the impostors re-stream. */
  setUplightProvider(fn: CloudUplightProvider | null): void {
    this._uplightProvider = fn;
    this._uplightGen++;
    this.refreshUplight();
  }
  get uplightProvider(): CloudUplightProvider | null { return this._uplightProvider; }
  get uplightGen(): number { return this._uplightGen; }
  /** Re-evaluate the provider for every cloud holding a cell (the provider's world changed — new sources). */
  refreshUplight(): void {
    for (const e of this._pool) {
      const v = e.u.uplight.value as THREE.Vector3;
      if (!e.cellKey) { v.set(0, 0, 0); continue; }
      const c = e.u.center.value as THREE.Vector3;
      this.uplightAt(c.x, c.z, v);
    }
  }
  /** The provider at an ABSOLUTE point into `out` — zeros without one; NaN / negative channels are 0 (never a black hole or a NaN cloud). */
  uplightAt(absX: number, absZ: number, out: { x: number; y: number; z: number }): { x: number; y: number; z: number } {
    let r = 0, g = 0, b = 0;
    const p = this._uplightProvider;
    if (p) {
      try {
        const u = p(absX, absZ);
        if (u) { r = u.r; g = u.g; b = u.b; }
      } catch { r = g = b = 0; }
    }
    const f = (v: number): number => (Number.isFinite(v) && v > 0 ? v : 0);
    out.x = f(r); out.y = f(g); out.z = f(b);
    return out;
  }

  /** Diagnostic shader view: RGB = rNorm / base-noise R / boundary gate at
   *  the chord midpoint (probe/console tool — see the clouddiag doctrine). */
  setDebugView(on: boolean): void {
    this._uDebug.value = on ? 1 : 0;
  }

  /**
   * Per-frame drive. `cam`/`scenePos` are RENDER-space (three.js) positions;
   * the field works in ABSOLUTE world space (render − scenePos), matching the
   * CloudRenderer rebase convention so clouds never re-arrange on FP64 snaps.
   */
  update(dt: number, cam: THREE.Vector3, scenePos: THREE.Vector3): void {
    if (!this._enabled) return;
    this._elapsed += dt * this._cfg.windTimeScale;
    this._windX = this._cfg.windX * this._elapsed;
    this._windZ = this._cfg.windZ * this._elapsed;
    this._evolve = this._cfg.evolveSpeed * this._elapsed;
    (this._uWind.value as THREE.Vector3).set(this._windX, 0, this._windZ);
    // Net-zero evolution direction (0.62, −0.79) — wind-incommensurate, same
    // convention as the deck; base drifts +, detail −.
    (this._uEvolve.value as THREE.Vector3).set(this._evolve * 0.62, 0, this._evolve * -0.79);
    (this._uWorldOffset.value as THREE.Vector3).set(scenePos.x, 0, scenePos.z);
    this._uTime.value = this._elapsed;

    const camAbsX = cam.x - scenePos.x;
    const camAbsZ = cam.z - scenePos.z;

    // Re-stream only when the camera crosses a cell boundary (or coverage
    // changed and reset the latch).
    const cellX = Math.floor(camAbsX / this._cfg.cellSize);
    const cellZ = Math.floor(camAbsZ / this._cfg.cellSize);
    if (cellX !== this._lastCellX || cellZ !== this._lastCellZ) {
      this._lastCellX = cellX;
      this._lastCellZ = cellZ;
      this._restream(camAbsX, camAbsZ);
    }

    // Mesh positions hold the ABSOLUTE center — the rebase convention: scene
    // children carry absolute coords and the scene's own transform
    // (scene.position = −origin) maps them to render space. Pre-adding
    // scenePos here applied the shift TWICE (matrixWorld = abs + 2·scenePos):
    // near spawn that displaced every raster sphere ~|scenePos| from its
    // shader-side analytic sphere (misaligned volume sampling — an orb-mess
    // co-cause), and after a real rebase it pushed the meshes hundreds of km
    // out of the frustum entirely.
    for (const e of this._pool) {
      if (!e.cellKey) {
        // A dying mesh keeps rendering its fade-out; once done, park it.
        if (e.dyingUntil > 0 && this._elapsed >= e.dyingUntil) {
          e.dyingUntil = 0;
          e.mesh.visible = false;
          (e.u.center.value as THREE.Vector3).set(0, -10000, 0);
          // A mesh came free for a cell that missed out: re-stream next frame.
          if (this._starved) this._lastCellX = Number.NaN;
        }
        continue;
      }
      const c = e.u.center.value as THREE.Vector3;
      e.mesh.position.set(c.x, c.y, c.z);
      // Distance LOD: a cloud at the field's edge spans a few dozen pixels —
      // march it with a longer step (fewer live samples; the loop's guard
      // skips the rest).
      const d = Math.hypot(c.x - camAbsX, c.z - camAbsZ);
      const t = Math.min(1, Math.max(0, (d - this._cfg.fieldRadius * 0.35) / (this._cfg.fieldRadius * 0.65)));
      e.u.lod.value = 1 + (this._cfg.lodFarStep - 1) * t;
    }
  }

  /** What every cloud of this field shares with the march (CloudMarch) — the far layer marches with it too. */
  get marchEnv(): CloudMarchEnv {
    return (this._marchEnv ??= {
      uniforms: this.uniforms, baseNoise: this._baseNoise, detailNoise: this._detailNoise,
      wind: this._uWind, evolve: this._uEvolve, worldOffset: this._uWorldOffset,
      noiseScale: this._uNoiseScale, extinction: this._uExtinction, windDir: this._uWindDir,
      airLow: this._uAirLow, airLowScale: this._uAirLowScale, airHigh: this._uAirHigh, airHighScale: this._uAirHighScale,
      hazeColor: this._uHazeColor, hazeDensity: this._uHazeDensity,
    });
  }
  private _marchEnv: CloudMarchEnv | null = null;
  // THE CLOUDS' AIR (CloudMarch.cloudAir): shared by the volumes and the far layer.
  private readonly _uAirLow = uniform(new THREE.Vector3(0, 0, 0));
  private readonly _uAirLowScale = uniform(1200);
  private readonly _uAirHigh = uniform(new THREE.Vector3(0, 0, 0));
  private readonly _uAirHighScale = uniform(8000);
  private readonly _uHazeColor = uniform(new THREE.Color(0.72, 0.78, 0.86));
  private readonly _uHazeDensity = uniform(0.00002);
  /**
   * The clear air between the eye and the clouds, as two exponential layers of the consumer's atmosphere: each a
   * SEA-LEVEL extinction per METRE per channel and a scale height in metres (a low aerosol layer; Rayleigh + whatever
   * rides high). The in-scattered light is the haze colour's. Zero extinction — the default — is no air.
   */
  setAir(low: { r: number; g: number; b: number }, lowScaleM: number, high: { r: number; g: number; b: number }, highScaleM: number): void {
    const f = (v: number): number => (Number.isFinite(v) && v > 0 ? Math.min(v, 0.01) : 0);
    const hs = (v: number, d: number): number => (Number.isFinite(v) && v > 1 ? v : d);
    (this._uAirLow.value as THREE.Vector3).set(f(low.r), f(low.g), f(low.b));
    (this._uAirHigh.value as THREE.Vector3).set(f(high.r), f(high.g), f(high.b));
    this._uAirLowScale.value = hs(lowScaleM, 1200);
    this._uAirHighScale.value = hs(highScaleM, 8000);
  }
  /**
   * Probe / debug: let `seconds` pass for every cloud at once. A cloud born in view condenses over ~40 s of the field's
   * own clock (frame dt, clamped) — hundreds of frames under a slow renderer; a probe that pins a sky ages it instead.
   */
  ageBy(seconds: number): void { if (Number.isFinite(seconds) && seconds > 0) this._elapsed += seconds; }
  /** The scene's haze (FogExp2's law) and the air's light: the horizon's colour in scene units. */
  setHaze(color: { r: number; g: number; b: number }, density: number): void {
    (this._uHazeColor.value as THREE.Color).setRGB(color.r, color.g, color.b);
    const d = Number.isFinite(density) ? density - this._hazeClearAir : 0;
    this._uHazeDensity.value = d > 0 ? d : 0;
  }
  /**
   * The part of the scene fog's density that is CLEAR AIR (default 0). A consumer that gives the clouds their clear air
   * through `setAir` names it here, so the fog's share is only what the weather adds — never the same air twice.
   */
  setHazeClearAir(density: number): void { this._hazeClearAir = Number.isFinite(density) && density > 0 ? density : 0; }
  private _hazeClearAir = 0;

  /** What the impostor layer shares with the field (uniform NODES — one driver surface). */
  get shared(): {
    uniforms: CloudMeshField['uniforms'];
    wind: ReturnType<typeof uniform>; evolve: ReturnType<typeof uniform>; worldOffset: ReturnType<typeof uniform>;
    time: ReturnType<typeof uniform>; fadeStart: ReturnType<typeof uniform>; fadeEnd: ReturnType<typeof uniform>;
    noiseScale: ReturnType<typeof uniform>; windDir: ReturnType<typeof uniform>;
    baseNoise: THREE.Data3DTexture; weather: { data: Uint8Array; size: number };
    cfg: { fieldRadius: number; cellSize: number; weatherScale: number; fadeStartRatio: number; impostors: CloudImpostorLayerConfig | false };
    formation: CloudLayerSpec[]; coverage: number;
    /** The uplight provider's generation (the impostors re-stream on a change) — 2026-10-06. */
    uplightGen: number;
  } {
    // One cached object, refreshed in place: the impostor layer reads this
    // EVERY frame and the literal used to allocate four objects a call.
    let s = this._shared;
    if (!s) {
      s = this._shared = {
        uniforms: this.uniforms,
        wind: this._uWind, evolve: this._uEvolve, worldOffset: this._uWorldOffset, time: this._uTime,
        fadeStart: this._uFadeStart, fadeEnd: this._uFadeEnd, noiseScale: this._uNoiseScale, windDir: this._uWindDir,
        baseNoise: this._baseNoise, weather: { data: this._weatherData, size: this._weatherSize },
        cfg: { fieldRadius: this._cfg.fieldRadius, cellSize: this._cfg.cellSize, weatherScale: this._cfg.weatherScale, fadeStartRatio: this._cfg.fadeStartRatio, impostors: this._cfg.impostors },
        formation: this._formation, coverage: this._coverage, uplightGen: this._uplightGen,
      };
    }
    s.weather.data = this._weatherData; s.weather.size = this._weatherSize;
    s.formation = this._formation; s.coverage = this._coverage; s.uplightGen = this._uplightGen;
    return s;
  }
  private _shared: CloudMeshField['shared'] | null = null;

  /** Recompute the per-étage cell→mesh mapping around (camAbsX, camAbsZ). */
  private _restream(camAbsX: number, camAbsZ: number): void {
    // Normalize pool shares, then gather each layer's desired cells with its
    // genus cell size, its own weather-sampling offset (so étages don't stack
    // identically), and near-bias only for the FIRST (low) layer.
    // (a far-only genus takes no share of the pool)
    const shareSum = this._formation.reduce((s, l) => s + (CLOUD_ARCHETYPES[l.kind].farOnly ? 0 : Math.max(0, l.share)), 0) || 1;
    const desired: Array<{ cell: DesiredCell; spec: CloudLayerSpec; arch: CloudArchetype }> = [];
    this._formation.forEach((spec, li) => {
      const arch = CLOUD_ARCHETYPES[spec.kind];
      if (arch.farOnly) return;
      const budget = Math.max(1, Math.floor(
        this._cfg.maxClouds * (Math.max(0, spec.share) / shareSum)));
      const layerCov = Math.max(0, Math.min(1, this._coverage * spec.coverage));
      if (layerCov < 0.02) return;
      const cells = computeDesiredCells(
        camAbsX, camAbsZ, layerCov,
        {
          fieldRadius: this._cfg.fieldRadius,
          cellSize: this._cfg.cellSize * arch.cellScale,
          maxClouds: budget,
          weatherScale: this._cfg.weatherScale,
          weatherOffset: { x: li * 3777.7, z: li * -2444.3 },
          nearBias: li === 0,
          keyPrefix: `L${li}:`,
          sticky: this._heldKeys,
          overcast: this._coverage,
        },
        { data: this._weatherData, size: this._weatherSize },
      );
      for (const cell of cells) desired.push({ cell, spec, arch });
    });
    const wanted = new Set(desired.map((d) => d.cell.key));

    // Release meshes whose cell fell out of the desired set — as a FADE, not
    // a cut (the one-frame `visible = false` was half of the popping bug; only
    // birth ever ramped). A dying mesh keeps rendering until its death fade
    // completes, and is only then recycled.
    const free: PoolEntry[] = [];
    for (const e of this._pool) {
      if (e.cellKey && !wanted.has(e.cellKey)) {
        e.cellKey = null;
        e.dyingUntil = this._elapsed + CloudMeshField.FADE_OUT_S;
        e.u.death.value = this._elapsed;
      }
      if (!e.cellKey && e.dyingUntil <= this._elapsed) free.push(e);
    }

    // Assign free meshes to new cells. Cells beyond the fade are already
    // invisible; nearer assignments fade in over ~3 s via uBirth.
    let starved = false;
    for (const { cell: d, spec, arch } of desired) {
      if (this._pool.some((e) => e.cellKey === d.key)) continue;
      if (free.length === 0) { starved = true; break; }
      const e = free.pop()!;
      const cellSize = this._cfg.cellSize * arch.cellScale;
      // ONE derivation for the volumetric mesh and the far sprite of a cell
      // (position, size, character, seed, turret layout) — the LOD hand-off
      // swaps representations of the SAME cloud.
      const cell = deriveCloudCell(d.cx, d.cz, cellSize, spec, arch, { data: this._weatherData, size: this._weatherSize }, this._cfg.weatherScale);
      const { absX, absZ, shape } = cell;
      e.cellKey = d.key;
      e.dyingUntil = 0;
      // The mesh sits at the BASE (the hemisphere's equator) and spans the dome height.
      (e.u.center.value as THREE.Vector3).set(absX, shape.baseY, absZ);
      e.u.radius.value = shape.radius;
      e.u.flatten.value = shape.flatten;
      (e.u.params.value as THREE.Vector3).set(cell.params.x, cell.params.y, cell.params.z);
      (e.u.seed.value as THREE.Vector3).set(cell.seed.x, cell.seed.y, cell.seed.z);
      const arr = e.u.lobes.array as THREE.Vector4[];
      for (let i = 0; i < MAX_LOBES; i++) { const l = cell.lobes[i]; arr[i].set(l.x, l.y, l.z, l.r); }
      this.uplightAt(absX, absZ, e.u.uplight.value as THREE.Vector3); // the ground's light under THIS cloud (2026-10-06)
      e.u.birth.value = this._elapsed;
      e.u.death.value = 1e9;
      // CONDENSATION vs FADE: a cell born inside the visible field of an
      // already-streamed sky (the coverage rose, the regime changed) grows
      // over growSeconds; the first fill and the far-edge arrivals (which
      // cross-fade with the impostor layer) keep the 3 s fade.
      const inView = this._everStreamed && d.dist < this._cfg.fieldRadius * this._cfg.fadeStartRatio;
      e.u.grow.value = inView ? this._cfg.growSeconds : 3;
      e.mesh.scale.set(shape.radius, shape.height, shape.radius);
      e.mesh.visible = true;
      // ABSOLUTE position — the scene transform maps it to render space
      // (see update()).
      e.mesh.position.set(absX, shape.baseY, absZ);
    }
    this._starved = starved;
    this._everStreamed = true;
    // Refresh the hysteresis sticky set to what actually holds a cloud now.
    this._heldKeys.clear();
    for (const e of this._pool) if (e.cellKey) this._heldKeys.add(e.cellKey);
  }

  dispose(): void {
    for (const e of this._pool) {
      const m = e.mesh.material;
      (Array.isArray(m) ? m : [m]).forEach((mm) => mm.dispose());
    }
    // The pool shares ONE merged dome geometry — released once; the group
    // was scene.add()ed by CloudModule and stayed an orphan child of the
    // scene after every teardown (2026-09-12).
    this._pool[0]?.mesh.geometry.dispose();
    this.group.clear();
    this.group.removeFromParent();
    this._pool.length = 0;
  }

  // ─── TSL material ─────────────────────────────────────────────────────────

  private _buildMaterial(u: MeshCloudUniforms): THREE.NodeMaterial {
    const S = this.uniforms;
    const baseNoiseTex = this._baseNoise;
    const STEPS = this._cfg.steps;
    const uWind = this._uWind;
    const uEvolve = this._uEvolve;
    const uWorldOffset = this._uWorldOffset;
    const uTime = this._uTime;
    const uFadeStart = this._uFadeStart;
    const uFadeEnd = this._uFadeEnd;
    const uNoiseScale = this._uNoiseScale;
    const uDebug = this._uDebug;

    // The cloud's shape and march are CloudMarch's — shared with the far layer, so a cloud is ONE picture at every
    // range. This mesh's parameters are its uniforms. u.center is the cloud's BASE point (the hemisphere's equator),
    // ABSOLUTE world (stable across FP64 rebase snaps); the march adds worldOffset (= scene.position) itself.
    // CONDENSATION: a cloud born in view starts with its skin retreated 0.5 of a turret radius and grows out over u.grow.
    const growT = clamp(uTime.sub(u.birth).div(u.grow), float(0), float(1));
    const env = this.marchEnv;
    const cm = buildCloudMarch({
      center: u.center, radius: u.radius, height: (u.radius as any).mul(u.flatten).mul(2),
      params: u.params, seed: u.seed, lobe: (i: number) => (u.lobes as any).element(int(i)), uplight: u.uplight,
      condense: float(1).sub(growT).mul(0.5), lod: u.lod,
    }, env, { steps: STEPS, sunSteps: this._uSunSteps });
    const { centerR, r3, lobeUnion } = cm;

    const marchNode = Fn(() => {
      // In a MESH material the ambient TSL camera accessors are the real
      // scene camera (the post-quad pitfall does not apply here).
      const ro = cameraPosition;
      // BackSide sphere ⇒ positionWorld is the sphere EXIT surface point.
      const rd = normalize(positionWorld.sub(ro));

      const centerDist = distance(ro, centerR());
      // NOTE: edges in ascending order — smoothstep(edge0>edge1) is
      // undefined in WGSL (the reversed form happened to work on GLSL).
      const distFade = float(1).sub(smoothstep(uFadeStart, uFadeEnd, centerDist));
      const ageFade = clamp(uTime.sub(u.birth).div(3), float(0), float(1));
      // Retirement mirror of the birth ramp: fades a released cloud out
      // over FADE_OUT_S instead of the old one-frame visible=false cut.
      const deathFade = float(1).sub(clamp(uTime.sub(u.death).div(2.5), float(0), float(1)));
      const lifeFade = ageFade.mul(deathFade);
      const r = cm.march(ro, rd, distFade.greaterThan(float(0.001))
        .and(lifeFade.greaterThan(float(0.001)))
        .and(S.coverage.greaterThan(float(0.02))));
      // Fade BOTH the premultiplied radiance and the alpha — fading
      // alpha alone under One/OneMinusSrcAlpha turns the layer ADDITIVE
      // (rgb keeps adding while dst stops darkening): every birth/edge
      // fade glowed as a bright ghost ball at dusk. Scaling rgb and a by
      // the same factor is the correct premultiplied fade.
      const fade = distFade.mul(lifeFade);
      // THE CLOUDS' AIR: seen through the air up to the dome's near side (a cloud you stand beside is not hazed).
      const airDist = max(centerDist.sub(float(u.radius as never)), float(0));
      const scat = cloudAir(env, r.scat, r.alpha, airDist, ro.y, rd.y).mul(fade), alpha = r.alpha.mul(fade);
      const { disc, b, a2 } = r;

      // Diagnostic view (uDebug=1): chord-midpoint rNorm / raw base-noise R /
      // boundary gate as RGB — the clouddiag "see WHICH stage is wrong" tool.
      const dbg = vec3(0).toVar('dbgRgb');
      If(uDebug.greaterThan(float(0.5)).and(disc.greaterThan(float(0))), () => {
        const sq = disc.sqrt();
        const tMid = max(b.negate().sub(sq).div(a2), float(0))
          .add(b.negate().add(sq).div(a2)).mul(0.5);
        const pMid = ro.add(rd.mul(tMid));
        const relM = pMid.sub(centerR()).div(r3());
        const lu = lobeUnion(relM);
        const sdfM = lu.sdf.div(max(lu.rNear, float(0.12)));
        const gateM = smoothstep(float(-0.45), float(0.05), sdfM).mul(1.6);
        const uvwM = pMid.sub(uWorldOffset).add(u.seed).add(uWind).add(uEvolve).mul(uNoiseScale);
        const baseM = texture3D(baseNoiseTex, uvwM).r;
        dbg.assign(vec3(clamp(sdfM.negate(), float(0), float(1)), baseM, clamp(gateM, float(0), float(1))));
      });

      return mix(vec4(scat, alpha), vec4(dbg, float(0.9)), uDebug);
    });

    const out = marchNode();
    const material = new THREE.NodeMaterial();
    material.side = THREE.BackSide;
    material.transparent = true;
    material.depthWrite = false;
    // SCENE FOG OFF — NodeMaterial.fog defaults TRUE, and the scene's
    // exponential fog then paints the ENTIRE bounding sphere by its SURFACE
    // depth: a uniform fog-colored disc stamped over every cloud regardless
    // of the density math (THE "orb around each cloud" — brown at night
    // because the fog color is). A volumetric medium does its own extinction;
    // per-sample aerial perspective is the v2 hook.
    material.fog = false;
    // Premultiplied OVER — same composite contract as the post-pass clouds:
    // final = src.rgb + dst·(1 − src.a).
    material.blending = THREE.CustomBlending;
    material.blendSrc = THREE.OneFactor;
    material.blendDst = THREE.OneMinusSrcAlphaFactor;
    material.colorNode = vec4(out as never);
    markMrtOverlay(material); // a volume in front of a mountain must not rewrite the mountain's normal (AO / SSR)
    return material;
  }
}
